import React from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        backgroundColor: 'rgb(239, 231, 221)',
        backgroundImage: 'url(https://www.transparenttextures.com/patterns/cream-paper.png)',
        backgroundRepeat: 'repeat'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 900,
          height: isMobile ? '90vh' : '80vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            position: 'sticky',
            top: 0,
            backgroundColor: 'inherit',
            zIndex: 1,
            borderBottom: '1px solid rgba(210, 166, 97, 0.5)'
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            General Terms & Conditions
          </Typography>
          <Typography variant="body2">
            <strong>Effective Date:</strong> {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            overflowY: 'auto',
            flex: 1,
            px: isMobile ? 2 : 3,
            pt: 2,
            pb: 1,
            '& .MuiTypography-body2': {
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              lineHeight: 1.5
            }
          }}
        >
          <Typography variant="body2" paragraph>
            These General Terms & Conditions (hereinafter 'Terms') constitute a legally binding agreement between you and the relevant Landnest entity (hereinafter 'Landnest/We/Us/Company') regarding your use of the web application/website www.landnest.net (hereinafter 'Site') and any Services offered or made available by Landnest, including delivery of content via the Site or through any mobile or internet-connected device (hereinafter 'Services'). Additionally, these Terms shall be applicable to all subsidiaries of Landnest Realty Services Limited and all other entities/Services where Landnest has significant control.
          </Typography>

          <Typography variant="body2" paragraph>
            Your use of the Site, Services, and tools is governed by the following Terms, which are incorporated herein. By mere use of the Site, you shall be contracting with Landnest. These Terms, including the policies, constitute your binding obligations with Landnest; therefore, it is imperative that before using the platform you acquaint yourself with the applicability and consequential application of these Terms.
          </Typography>

          <Typography variant="body2" paragraph>
            When you use any of the Services provided by Landnest through the Platform, including Service Reviews, etc., you will be subject to the rules, guidelines, policies, terms, and conditions applicable to such Services, and they shall be deemed to be incorporated into these Terms and shall be considered as part and parcel thereof.
          </Typography>

          <Typography variant="body2" paragraph>
            Landnest reserves the right to amend or modify these Terms without any prior notice or intimation. Such modifications shall become effective immediately upon being posted on the Site. You are advised to review the Terms periodically to remain informed of any changes. Any further access to the Site after such modifications shall constitute your conclusive acceptance of the revised Terms.
          </Typography>

          <Typography variant="body2" paragraph>
            Landnest also reserves the right to temporarily suspend operations of the Site for maintenance, technical upgrades, content updates, or any other purpose, without prior notice to the user.
          </Typography>

         <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  I. Defined Terms
</Typography>
<Typography variant="body2" paragraph>
  Unless otherwise stated, the capitalized terms used herein shall have the meanings assigned to them below:
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Agreement" 
      secondary="means the completed application form, together with its attachments and these Terms, and shall be deemed executed at Chittoor." 
      secondaryTypographyProps={{ component: 'span' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="LANDNEST" 
      secondary="refers to Landnest, a company incorporated under the Companies Act, 1956, having its registered office at Residing at No 4-2058 Rami Reddy Layout,Siddhartha Opp-Pvkn College Nagar Of The Chittoor 517001. Hereinafter called as 'TENANTS' other part. including its unit operating the website Landnest.net." 
      secondaryTypographyProps={{ component: 'span' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Date of Commencement" 
      secondary="means the date on which the User accepts and subscribes to the Services." 
      secondaryTypographyProps={{ component: 'span' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Date of Termination" 
      secondary="means the date specified in the notice and/or letter/or auto generated/scheduled app notifications of termination, or the date on which the Services are contractually set to expire, whichever is applicable." 
      secondaryTypographyProps={{ component: 'span' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Landnest.net or Site" 
      secondary="means the internet website and mobile application operated by Landnest, accessible at www.Landnest.net." 
      secondaryTypographyProps={{ component: 'span' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Subscriptions Plans" 
      secondary="means the plan selection that provides descriptions and details of the Services availed by the User, whether displayed on the Site/App or otherwise communicated in writing to Landnest." 
      secondaryTypographyProps={{ component: 'span' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Registration Data" 
      secondary="means the database containing all particulars and information provided by the User at the time of application and subscription, including but not limited to User name, contact details, mailing address, account details, and any other information supplied." 
      secondaryTypographyProps={{ component: 'span' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="User or You" 
      secondary="means any Subscriber, Advertiser, Browser, or Visitor, and user or whether an individual or a legal entity, whose details are recorded in the application form, and includes their successors and permitted assignees. The term further includes any person accessing or using the Services for purposes such as hosting, publishing, listing, transacting, or viewing information, or otherwise participating in any manner." 
      secondaryTypographyProps={{ component: 'span' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Confidential Information" 
      secondary="means all non-public, proprietary, technical, financial, strategic, commercial, operational, or business-related information, data, documents, trade secrets, know-how, customer lists, pricing strategies, product designs, marketing plans, and any other information disclosed by Landnest or its affiliates, in any form or medium (oral, written, electronic, or otherwise), whether or not marked as confidential, to the other party in connection with these Terms." 
      secondaryTypographyProps={{ component: 'span' }}
    />
  </ListItem>
</List>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  II. Provision of Services
</Typography>
<Typography variant="body2" paragraph>
  Landnest provides various internet-based services through its platform, including but not limited to:
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Posting property listings and User profiles for sale, rent, lease, Joint development, Joint venture, Constructions, Interiors, Home services or any related services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Searching properties via its website and mobile apps." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Placing print advertisements in group publications via the platform." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Publishing online advertisements." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Sending promotional messages and communications through email, SMS, or other permitted channels." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Any other services as may be made available on the Site or as otherwise set forth in these Terms." />
  </ListItem>
</List>
<Typography variant="body2" paragraph>
  All services may be availed through the payment options made available on the platform and shall be subject to the applicable policies of Landnest, including but not limited to subscription fees, refund rules, and cancellation terms.
</Typography>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  III. Eligibility
</Typography>
<Typography variant="body2" paragraph>
  By accessing or using the Services, you represent and warrant that you are at least 18 years of age, legally competent to enter into a valid and binding contract, and fully capable of complying with these Terms.
</Typography>
<Typography variant="body2" paragraph>
  Minors (individuals under the age of 18) may access or use the Services only under the supervision of a parent or legal guardian, and solely through the guardian's registered account.
</Typography>
<Typography variant="body2" paragraph>
  You further agree that, prior to uploading any content or accessing the Services, you shall complete the registration process and provide accurate, current, and complete personal details, including but not limited to your name, age, email address, residential address, and contact number.
</Typography>
<Typography variant="body2" paragraph>
  Landnest reserves the right to verify the information provided by You at any time. If any information is found to be false, inaccurate, misleading, or incomplete, Landnest may, at its sole discretion, suspend, restrict, or terminate your account and deny you access to the Services without prior notice.
</Typography>
     <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  IV. Subscription Plans
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest offers a combination of Services which the User may subscribe to, subject to the need of the User, upon payment of applicable fees." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The subscription fee paid by the User is non-refundable." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest reserves the right to revise the fee of any subscription plan without notice to the User at any time prior to, at the time of, during the tenor, or post subscription of the plan by the User. Landnest shall intimate the User of such revised fees, and the User undertakes to pay to Landnest the difference in the amount after such revision." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The Listings will be based and limited to the Choice of subscription plan availed by the user." />
  </ListItem>
</List>

    <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  V. Payment & Refund
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="For all Services, 100% of the total order value will be treated as activation/administration charges. It is non-refundable at behest." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All refund claims shall be subject to the sole discretion of Landnest and shall not be construed as an automatic right." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users are required to pay the subscription fees within the time period specified in the invoice(s) issued by Landnest. Subscription fees are payable by the User on demand." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="In the event of any dispute regarding the amount invoiced, the User shall first pay the full invoiced amount pending resolution. If Landnest subsequently determines that the dispute is valid, any excess amount paid by the User shall be refunded without interest." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Any delay by the User in making payments will attract interest on the outstanding amount from the due date until the date of full payment." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All payments made through the Website/Android/iOS application are strictly non-refundable under any circumstances, including termination of these Terms for any reason." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="If a User is found to be a Builder/Agent instead of a property Owner, the listing shall be deactivated without notice and the entire amount paid shall be forfeited." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All the payments made for the module/section/category which denotes prime property and is a separate transactional highlight posted through the admin panel/company is required to pay the full amount in advance and the 50% of the total order value shall be treated as activation/administration charges. The remaining 50% may be refundable on a pro-rata basis upon cancellation, depending on the extent of Services already availed." />
  </ListItem>
</List>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  VI. Charge Back Policy
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Payment for Services shall be made on a 100% advance basis." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Once subscribed, all payments are non-refundable and shall be appropriated on the discretion of Landnest." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Set-Off Rights: LANDNEST may, at its discretion, set off any payment made by the User against amounts payable under other agreements or Services with LANDNEST." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest does not guarantee the accuracy, timeliness, or successful completion of refunds to the User's bank account or card, due to the involvement of multiple financial entities and system limitations." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="By subscribing to the Services, the User expressly agrees not to initiate any chargeback request with their bank, card issuer, or payment provider for payments made to Landnest. Any chargeback initiated in violation of this clause shall be treated as a breach of these Terms, entitling Landnest to suspend or terminate the User's account and recover all associated costs, including legal and collection expenses." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  VII. Cancellation
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest reserves the sole and absolute right to refuse, cancel, suspend, or withhold the publication of any content on its platform at any time, with or without prior notice, if such content is found to be unlawful, misleading, fraudulent, objectionable, or in violation of these Terms or applicable laws." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="In such cases, applicable cancellation charges shall be payable by the User in accordance with the Landnest Cancellation and Refund Policy." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="In cases where payment is made via cheque or demand draft, cancellations will be accepted only prior to the realization of the payment. Once the payment has been realised, no cancellation or refund shall be permitted." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Landnest further reserves the right to cancel or suspend a User's subscription or access to Services, without refund, in cases of:"
      secondary={
        <List dense sx={{ pl: 2, mt: 1 }}>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Misuse of the platform, including fraudulent listings or impersonation." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Non-compliance with these Terms or applicable laws." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Providing false, inaccurate, or misleading information at the time of registration or thereafter." />
          </ListItem>
        </List>
      }
      secondaryTypographyProps={{ component: 'div' }}
    />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  VIII. Security
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All transactions conducted on the Site are encrypted and secured. Credit/debit card details and other payment information are handled solely by authorized third-party payment gateways. Landnest does not store or retain such information. Users provide payment details directly to the relevant payment gateway, which is authorized to process such information and is required to comply with the regulations and requirements of banks, financial institutions, and payment networks." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User is responsible for safeguarding their username and password, including changing the password periodically and ensuring that login credentials are not shared with any third party." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Access to the Services must be strictly through the User's own credentials. Login credentials are intended exclusively for the User or their authorized representatives, and the User must take all necessary precautions to prevent unauthorized access or disclosure." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users acquire no ownership rights over mailbox numbers, User IDs, or codes assigned by Landnest. Landnest may, at its sole discretion, reassign or withdraw such identifiers without liability for any loss, damages, or claims." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="In the event of loss or theft of login credentials, the User must notify Landnest immediately via email, WhatsApp, or any other approved communication channel. The User shall remain responsible and liable for all activities carried out using their account until Landnest receives such notification and confirms account suspension." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The use of automated software, bots, or scraping tools to extract, harvest, or download data from the Site is strictly prohibited without Landnest's prior written consent. Any violation shall constitute misuse and may result in suspension, termination, and legal action." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest shall not be liable for any fraudulent transactions, unauthorized deductions, or misuse of the User's bank account, card, or payment information, whether arising from the User's negligence, compromise of login credentials, or third-party actions beyond Landnest's reasonable control." />
  </ListItem>
</List>
         <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  IX. Obligations and Representations of the User
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall provide accurate, complete, and correct Registration Data at the time of applying for the Services and shall update such data as necessary to ensure its accuracy at all times." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User acknowledges that all data entered on the Site shall be subject to verification by Landnest." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall, at their own cost, obtain and maintain all necessary licenses, permits, consents, approvals, and intellectual property rights required for using the Services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall comply with all instructions, directions, or notices issued by Landnest in relation to the usage of the Services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall be solely responsible for all applicable taxes, duties, and related costs incurred in connection with the use of the Services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall be solely responsible for all information retrieved, stored, published, displayed, or transmitted through the Services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall maintain the confidentiality of their login credentials and all activities conducted under their account." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall promptly notify Landnest of any unauthorized access, misuse, or security breach of their account and shall ensure proper logout after each session." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall make timely payment of all Subscription Fees to Landnest as and when they become payable." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User confirms that they are above 18 years of age and legally competent to contract." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall be solely responsible for the set-up, configuration, and compatibility of their equipment, devices, or systems necessary to access the Services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User acknowledges that listings may take up to 48 hours to reflect on the Site from the time of submission." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User consents to Landnest's use, storage, processing, and commercial exploitation of submitted data in compliance with the provisions of the Digital Personal Data Protection Act, 2023 and other applicable laws." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User grants Landnest a non-exclusive, worldwide, royalty-free, irrevocable, and sub-licensable license to use, reproduce, display, distribute, and publish content submitted by the User (hereinafter 'User Data') across media platforms, excluding sensitive financial data. For the purposes of these Terms, 'Individually Identifiable User Data' means that subset of User Data which can reasonably be used to identify a specific individual such as their name, address, phone number, etc. User Data shall be deemed to be the property of the Site." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall take all reasonable efforts to ensure that User Data is accurate, complete, lawful, and not misleading in any manner. The User shall bear sole responsibility for the accuracy, reliability, authenticity, and genuineness of all User Data." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User represents, warrants, and undertakes that all data submitted is truthful, accurate, correct, and lawfully owned by the User. The User further represents that no third-party rights, including intellectual property rights, are infringed by the submission or publication of such data." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="In case of any claim, dispute, or legal action arising out of or relating to the User Data, including claims of data theft, infringement, misrepresentation, or third-party rights, the User shall be solely responsible for defending and resolving the same at their own cost."
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="The User hereby indemnifies and agrees to hold harmless Landnest, its affiliates, directors, employees, and agents from and against any and all claims, damages, liabilities, costs, expenses, or losses (including legal fees) arising directly or indirectly from:"
      secondary={
        <List dense sx={{ pl: 2, mt: 1 }}>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Submission or publication of inaccurate, false, or infringing User Data;" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Violation of any intellectual property or third-party rights; or" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Misuse of the Services by the User or through their account." />
          </ListItem>
        </List>
      }
      secondaryTypographyProps={{ component: 'div' }}
    />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  X. Prohibited Actions
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall not allow any unauthorized person(s) to access, use, or exploit the Services. The User shall not resell, sublicense, assign, transfer, or commercially exploit the services, listings, or any part thereof, except as expressly permitted by Landnest." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The Services shall only be used for their intended and subscribed purpose, in full compliance with applicable Indian laws, rules, regulations, and these Terms." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall not create, upload, transmit, display, or store any content that is defamatory, obscene, pornographic, pedophilic, abusive, threatening, harassing, libelous, racially or ethnically objectionable, profane, invasive of privacy, or otherwise unlawful, harmful, or objectionable in any manner." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User is strictly prohibited from creating or distributing content that endangers the sovereignty, integrity, security, or unity of India; harms friendly relations with foreign states; disturbs public order; or incites the commission of cognizable offences." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall not impersonate any person or entity, misrepresent their identity, or falsely state or misrepresent their affiliation with any person, entity, or organization. The User shall not post, transmit, or disclose confidential, proprietary, or sensitive information without proper authorization." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall not upload, introduce, or transmit any viruses, malware, trojans, ransomware, or malicious code, nor attempt unauthorized access to the Site, Services, servers, systems, or networks. The use of automated bots, scrapers, crawlers, or similar tools without the prior written consent of Landnest is strictly prohibited." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall not print, download, delete, amend, misuse, or otherwise exploit any data, listings, or content posted by other Users. Use of third-party data without consent is prohibited, except strictly for lawful and authorized transactions." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall not post, share, or distribute any false, misleading, fraudulent, or deceptive information, nor any content intended to maliciously harm, defame, or misinform other Users." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall not engage in unsolicited messaging, spamming, phishing, or bulk communications, including but not limited to promotion of unrelated services, redirection from other platforms, or advertising without consent." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Any violation of these prohibitions may result in, at Landnest's sole discretion:"
      secondary={
        <List dense sx={{ pl: 2, mt: 1 }}>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Suspension, restriction, or permanent deactivation of the User's account;" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Forfeiture of subscription fees as liquidated damages; and/or" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Legal action for recovery of losses, claims, or damages suffered by Landnest or its Users." />
          </ListItem>
        </List>
      }
      secondaryTypographyProps={{ component: 'div' }}
    />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XI. Intellectual Property Rights and Data Usage
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All intellectual property in the Site, Services, listings, and related content belongs exclusively to Landnest. Users gain no ownership or rights." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Any content submitted by Users (text, images, videos, reviews, etc.) automatically becomes the property of Landnest. Unauthorized use will invite legal action." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All trademarks, logos, and brand elements on the Site are owned or licensed by Landnest. No User may use them without written consent." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may suspend or terminate access without notice for infringement of its or third-party rights." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users receive a limited, revocable, non-transferable license to use the Services strictly as per these Terms." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="No part of Landnest's proprietary content may be copied, modified, or reused without prior written approval." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may use, process, and store User Data for business, legal, and marketing purposes, subject to law and privacy policy." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="User Data remains with Landnest even after account suspension or termination." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="By sharing contact details, Users consent to receive calls, SMS, WhatsApp messages, and alerts from Landnest and its partners, regardless of DND registration." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may record and use communications with Users for business and compliance purposes." />
  </ListItem>
</List>
<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XII. Confidentiality
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users may receive non-public, sensitive, or proprietary information of Landnest, its affiliates, partners, or other users. Such information must be kept strictly confidential." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users shall not disclose, share, publish, or use such information without prior written consent of Landnest, except where disclosure is required by law." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Confidential information may be used only as necessary for accessing and using the Services in accordance with these Terms." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users shall take reasonable steps to protect such information from unauthorized use or disclosure." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="These obligations survive termination or expiry of the User's access to the Services." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XIII. Amendments
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest reserves the exclusive right to amend, vary, or modify these Terms and any Service guides at any time." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All updates or changes will be posted on the Site, and Users are responsible for reviewing the latest Terms regularly." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Continued use of the Services constitutes acknowledgment and acceptance of all amended Terms." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="No separate notice or communication is required for any amendments." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XIV. Services Modification, Discontinuation, and Maintenance
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest reserves the sole discretion to add, modify, suspend, or discontinue any part of the Services, temporarily or permanently, with or without prior notice." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="For paid Services, any pro-rated refund for unused portions may be provided at Landnest's sole discretion." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may temporarily deactivate, restrict, or suspend access to the Site or Services at any time for maintenance, upgrades, testing, repairs, or other technical purposes, without prior notice." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest shall not be liable for any direct or indirect loss, damage, cost, or expense arising from modification, suspension, or discontinuation of the Services, including during maintenance. No fees paid by the User shall be refunded, rebated, or adjusted unless expressly agreed by Landnest." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XV. Third-Party Links and Resources
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The Site may display or link to content, Services, advertisements, or goods provided by third-party websites or platforms. Landnest does not control and is not responsible for their availability, accuracy, content, privacy, or quality. Accessing such third-party sites is at the User's sole risk." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Hyperlinks, references, or listings of third-party Services are provided solely for User convenience and do not imply endorsement or affiliation by Landnest." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest makes no warranties regarding third-party Services, products, or content, including merchantability, fitness for purpose, or performance, and shall not be liable for any loss, damage, or harm arising from the use of such Services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Any commercial, contractual, or transactional relationship with a third-party provider is strictly between the User and the provider. Landnest is not responsible for delivery, quality, satisfaction, non-performance, misconduct, or disputes related to third-party Services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may provide limited support to third-party providers, but such support does not create liability toward Users." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users integrating or enabling third-party Services authorize the provider to access their data only for functional purposes, and Landnest shall not be liable for any data breach, corruption, or loss resulting from such integrations." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users shall indemnify and hold harmless Landnest, its affiliates, officers, employees, agents, and partners from any claims, liabilities, losses, or damages arising from their use of third-party Services or interactions with third-party providers." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest does not offer refunds for goods or services purchased from third-party providers. All refund or grievance claims must be addressed directly to the respective provider." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XVI. Termination and Suspension
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users may request suspension of Services by submitting a written request. Landnest shall review and respond within 30 days. Suspension or termination is at Landnest's sole discretion and confirmed in writing." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may immediately terminate access to the Services without prior notice if it reasonably believes the User has engaged in fraud, misrepresentation, cheating, or any act prejudicial to Landnest's interests." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Upon termination or suspension, all fees paid are forfeited and non-refundable. Users expressly waive any claims for refund, compensation, or damages in case of immediate termination." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Provisions intended to survive termination—including confidentiality, limitations of liability, and dispute resolution—shall continue in effect." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XVII. Suspension of Services
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="If any amounts payable by the User are not paid by the due date, Landnest may suspend the Services without prejudice to its other rights or remedies." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Suspension of Services shall be deemed termination, and the User remains liable for all charges and fees incurred up to the termination date." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may, at its sole discretion and on such terms as it deems appropriate, restore Services upon full payment of outstanding amounts." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XVIII. Money-Back Policy
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Refund requests must correspond to the original Order ID and Customer Name. Refunds are processed only to the name used at booking." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="For online payments, refunds will be made after deducting subscription fees, payment gateway charges, referral charges and applicable taxes." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Refunds will be processed within 30 business days of receipt, verification, and acceptance of all required documents." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XIX. Violation of Terms
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may, at its sole discretion, take action against any User violating these Terms, including deleting content, suspending or canceling accounts, and excluding Users from the platform." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may pursue violators under applicable civil or criminal laws and cooperate with any investigation by competent authorities, without prior notice." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="If Landnest believes any listing or Service may create liability, it may take necessary steps to mitigate risk, including disclosing User information." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest reserves the right to refuse Services or remove listings at any time for any reason, without prior notice." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XX. Errors, Inaccuracies, and Omissions
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The Site and Services may contain typographical errors, inaccuracies, or omissions, including in descriptions, pricing, offers, or availability." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest reserves the right to correct, update, modify, or cancel any information or orders at any time without prior notice, including after submission." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest is not obligated to update, amend, or clarify information except as required by law. References to update dates do not guarantee that all information is current or accurate." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  LEGAL DISCLAIMER & LIMITATION OF LIABILITY
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="No Warranty of Service Performance: Landnest expressly disclaims all warranties, whether express or implied. Landnest does not warrant or represent that:"
      secondary={
        <List dense sx={{ pl: 2, mt: 1 }}>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="the Services shall meet the specific requirements or expectations of the User;" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="the Services shall be uninterrupted, timely, secure, or error-free;" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="the results obtained from the Services shall be accurate, complete, or reliable;" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="the quality of any products, services, information, or material obtained through the Services shall meet the User's expectations; or" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="any errors, defects, or deficiencies in the Site, Services, or underlying software shall be corrected." />
          </ListItem>
        </List>
      }
      secondaryTypographyProps={{ component: 'div' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Data Integrity & Site Availability: The Site may be subject to data corruption, tampering, unauthorized access, viruses, or transmission errors. Landnest shall not be liable for any consequences arising therefrom. The Site may be rendered temporarily unavailable due to maintenance, upgrades, or unforeseen technical issues, without prior notice. Landnest further does not warrant that third-party or linked websites are free from harmful components including viruses, trojans, or worms." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Accuracy of Information: Landnest makes no warranties regarding the accuracy, reliability, completeness, or quality of any information, content, or data obtained through the Site or Services. Users access and utilize such information strictly at their own discretion and risk." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Role as Intermediary: Landnest operates strictly as an 'intermediary' as defined under Section 2(w) of the Information Technology Act, 2000. The content hosted on the Site is provided on an 'as-is' basis, without warranties of any kind. Views or opinions expressed by Users are solely their own and shall not be deemed to represent or be endorsed by Landnest." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="No Guarantee of Response or Leads: Landnest does not guarantee any level of response, satisfactory or otherwise, to any listings, banners, or advertisements displayed on the Site. Landnest does not control or warrant the quality, quantity, timeliness, or conversion of leads that may be generated." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Verification of Pre-Launch Offers: Users are advised to independently verify the authenticity of pre-launch offers. Landnest does not endorse or recommend investment in any project which does not have requisite official sanction." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="No Offer of Investment: Nothing contained on the Site shall constitute an offer, solicitation, or inducement to invest in Landnest or any of its affiliates, nor shall it constitute any representation regarding the use, safety, or patent compliance of any product or service." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Accuracy of User Information: Landnest shall not be responsible or liable for any disclosure, error, omission, or inaccuracy in the information provided by Users. All data is accepted in good faith, and the responsibility for authenticity of such information lies solely with the User." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Third-Party Services: Landnest shall not be held responsible for the quality, availability, accuracy, or performance of any third-party services made available on, through, or beyond the platform. Users shall engage with such third-party providers independently and at their sole risk." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Property Descriptions & Estimates: Property descriptions, value estimates, and related content displayed on the Site are provided strictly for marketing and informational purposes. Buyers, tenants, and agents are solely responsible for verifying accuracy and suitability. Users are advised to obtain independent professional advice before making any commercial or investment decisions." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Delivery of Communications: Landnest shall not be responsible for any delays, failures, or errors in message delivery occurring through third-party gateways or service providers." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="User Compliance with Law: Users shall, at all times, comply with applicable laws while using the Services. Landnest shall not be liable for any default, contravention, or violation committed by any User." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Intellectual Property Infringement: In the event any content hosted on the Site infringes intellectual property rights, Users are required to notify Landnest promptly in writing, with supporting documentation." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Editorial Control & Removal of Content: Landnest does not exercise prior editorial control over User-generated content. However, Landnest reserves the right to remove, disable, or modify any content, at its discretion, to comply with applicable laws, regulatory requirements, or community standards." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Limitation of Liability: Landnest's total liability under this Agreement, whether in contract, tort, or otherwise, shall not exceed the total subscription fees actually paid by the User to Landnest during the two (2) months immediately preceding the event giving rise to such claim." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Expert and Professionals Advice & Work: Any advice, opinion, or recommendation and work done and expressed by experts and professionals through the Site and on the site is strictly their own and is not verified or endorsed by Landnest. It is only a medium of communication connecting the users and the third party intermediary. Users must independently verify such information prior to relying upon it." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Intermediary Role in Transactions: Landnest is solely an advertising and intermediary platform. It is not a party to, nor responsible for, any transaction, agreement, performance, breach, or delivery between Users. All disputes arising between Users must be resolved independently and without recourse to Landnest." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Dispute Disclaimer: In the event of disputes between Users, Landnest shall have no obligation to mediate, intervene, or provide any dispute resolution services, and expressly disclaims any liability arising therefrom." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Exclusion of Damages: Under no circumstances shall Landnest and its affiliates be liable for any direct, indirect, incidental, consequential, or punitive damages, including loss of profits, goodwill, data, or business opportunities, arising out of or in connection with the use of the Site or Services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Third-Party Data Sharing: By availing any third-party services, Users expressly authorize Landnest to share their data with such service providers. Landnest disclaims all responsibility for any misuse, unauthorized access, or loss of data by third parties." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="No Investment Advice: Landnest does not provide investment, financial, or legal advice. All information displayed on the Site is for informational purposes only and does not constitute an offer or solicitation. Real estate investments involve inherent risks and should be made only after independent due diligence." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  Terms and conditions for referral
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Benefits for Referee (person getting subscription referral): Get minimum 10% off on subscription referral and not login referral." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Each person referring his/ her friends or family will get a percentage on the successful subscription referral" />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The Referee shall be eligible only for subscription referral only upon payment of the subscription amount." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The subscription referral shall be awarded within 30 days of successful referral of a new subscriber." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="An individual can refer to any number of people and be awarded with subscription referral bonus in a year." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The condition is only valid in cities where Landnest is operational." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest reserves the rights to modify the offer without prior information to the public." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest reserves the right to interpret the offering in case of ambiguity." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Rights to interpret ambiguities in the T&C shall lie exclusively with Landnest." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The condition for referral is only valid if the referee has a subscription in his name." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXIII. Indemnity
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="The User agrees to indemnify, defend, and hold harmless Landnest, its directors, officers, employees, and agents from any claims, actions, or proceedings arising from:"
      secondary={
        <List dense sx={{ pl: 2, mt: 1 }}>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Any advertisement, property listing, or content posted by the User;" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Unauthorized or wrongful posting of property;" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Infringement of copyright, trademark, trade secret, or other intellectual property rights of third parties;" />
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 1 }}>
            <ListItemText primary="Violation of any applicable law or agreement." />
          </ListItem>
        </List>
      }
      secondaryTypographyProps={{ component: 'div' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User shall reimburse Landnest for any losses, damages, costs, or liabilities incurred directly or indirectly due to the User's actions or use of the platform." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXIV. Notice
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText 
      primary="Grievance Redressal: Any complaints, abuse, or concerns regarding content or breach of these Terms must be reported immediately to the designated Grievance Officer:"
      secondary={
        <Box sx={{ pl: 2, mt: 1 }}>
          <Typography variant="body2"><strong>Grievance Officer:</strong> Mr. Gagan.R</Typography>
          <Typography variant="body2"><strong>Email:</strong> landnest.team@gmail.com</Typography>
          <Typography variant="body2"><strong>Address:</strong> #26, 2nd Floor, Above HDFC Bank, Devatha Mansion, 5th Main Road, Gandhinagar, Bangalore-560 009</Typography>
        </Box>
      }
      secondaryTypographyProps={{ component: 'div' }}
    />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Notices may be sent in writing or via email and will be considered received when delivered to the above address or email." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXV. Non-Exclusive Remedy
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Termination or expiration of this Agreement, in whole or in part, does not limit either party from pursuing other available remedies." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Termination does not relieve the User from paying all fees due up to the effective date." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Neither party shall be liable to the other for damages resulting solely from such termination, as permitted under these Terms." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXVI. Waiver
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Failure by Landnest to enforce any provision of these Terms does not constitute a waiver of that provision or right." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="If any provision is deemed invalid by a competent court, the remainder of the Terms shall remain in full force." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXVII. Entire Agreement
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="These Terms constitute the complete and exclusive agreement between the User and Landnest, superseding all prior agreements or communications." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Modifications or waivers of rights must be in writing and executed by Landnest only." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The right to amend these Terms rests solely with Landnest." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXVIII. Governing Law and Jurisdiction
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="No agency, partnership, joint venture, employment, or franchise relationship exists between Landnest and the User." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Any claim or cause of action related to the Services must be filed within 30 days of its occurrence, or it is barred." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="User information is provided in good faith and believed to be accurate and lawful." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="These Terms and any disputes arising from the use of the Services are governed by the laws of India, and the parties submit to the exclusive jurisdiction of the courts at Bengaluru, Karnataka." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXIX. Acknowledgment & Acceptance
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="These Terms are the complete agreement between the User and Landnest, replacing all prior arrangements." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="By registering or checking 'I accept the Terms,' the User agrees to be bound by them." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users are encouraged to report any material or behavior that may violate the Terms to help improve Services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="We are merely a technology platform service provider." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXX. Privacy Policy
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest respects User privacy and protects personal information." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Data such as property details, names, and emails is collected voluntarily and stored securely." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Data is used for property-related purposes and may be shared with clients; unauthorized use by third parties is prohibited." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Legal action and indemnification may apply for misuse of data." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXXI. PropWorth Disclaimer
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Information on the Site is for general guidance only and is not legal, financial, or real estate advice." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Accuracy is aimed for but not guaranteed; Users must verify independently and seek professional advice." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest is not liable for any losses, errors, or misunderstandings." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All measurements, prices, and property details are approximate and subject to change." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXXIII. Legal Metrology Disclaimer
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Property areas must be declared in square meters (m²) as required by the Legal Metrology Act, 2009." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Any misrepresentation or use of non-standard units (e.g., sq. ft., gaj, bigha) violates the law." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users are responsible for accuracy and legal compliance; Landnest is only a platform and disclaims liability for non-compliance." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Misleading listings may be suspended, modified, or removed by Landnest without notice." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXXIV. RERA Disclaimer
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Property listings must comply with the RERA Act, 2016, including registration and statutory disclosures." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest does not verify RERA compliance; Users must independently confirm details via official channels." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest is not a real estate agent and is not responsible for transaction outcomes, project quality, or deliverability." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXXV. Rent and Lease Agreement Disclaimer
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest facilitates rent agreements between Users and landlords but is not a legal agent or intermediary." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users submit personal details voluntarily and consent to being contacted by authorized service providers." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Compliance with laws (e.g., stamp duty, registration) is the responsibility of the User and landlord." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest does not verify ownership, property condition, or legal compliance." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Users must indemnify Landnest against any claims or disputes arising from rent transactions." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All orders are final and non-refundable, with limited exceptions where Landnest may offer a resolution." />
  </ListItem>
</List>



         <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XXXVII. Home Interiors and Construction or Renovation
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest facilitates connections between customers and home interior service providers/Construction/Renovation ('Channel Partners')." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may help identify providers and finalize quotation, but has no role in execution, quality, or completion of the work." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All commercial terms (price, timelines, payments) are strictly between the customer and the Channel Partner." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Disputes, delays, deficiencies, or damages arising from the service are the responsibility of the Channel Partner." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Customers waive any right to claim against Landnest for issues related to Channel Partner services." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Terms are governed by Indian law and under the jurisdiction of courts in Bengaluru." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest may update these terms at any time without notice." />
  </ListItem>
</List>

<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
  XL. Property Inspection
</Typography>
<List dense sx={{ pl: isMobile ? 1 : 2 }}>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Channel Partners or service providers means the entity providing property inspection services through the Site." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="User means any individual or entity purchasing property inspection services via the Site and voluntarily sharing property details." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest acts only as a connector between the User and Channel Partners or service providers; transactions are principal-to-principal." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="All confidential information shared by the User is at their own risk. Users authorize Service Providers or Channel Partners to contact them via phone, email, or WhatsApp. Use of data is governed by the Landnest Privacy Policy." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest screens Service Providers but does not guarantee service quality and is not liable for deficiencies." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="The User must verify and evaluate the inspection report. Any issues must be addressed directly with the Service Provider or Channel Partners. Landnest may assist but is not responsible for final resolution." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="Landnest is not a party to disputes between the User and Channel Partners or service provider. The User agrees to indemnify Landnest against any claims." />
  </ListItem>
  <ListItem sx={{ display: 'list-item', pl: 1 }}>
    <ListItemText primary="We are merely a technology platform service provider." />
  </ListItem>
</List>

<Typography variant="body2" paragraph sx={{ fontStyle: 'italic', mt: 2 }}>
  By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
</Typography>
</Box>

        {/* Footer Buttons */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            borderTop: '1px solid rgba(210, 166, 97, 0.5)',
            backgroundColor: 'inherit'
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate('/')}
            sx={{
              color: 'text.primary',
              borderColor: 'text.primary',
              '&:hover': {
                borderColor: 'text.primary',
                backgroundColor: 'rgba(210, 166, 97, 0.2)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/')}
            sx={{
              backgroundColor: 'text.primary',
              color: 'background.paper',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)'
              }
            }}
          >
            I Accept Terms & Conditions
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default TermsAndConditions;